import { useState } from "react";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import PeopleIcon from "@mui/icons-material/People";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CampaignIcon from "@mui/icons-material/Campaign";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import HistoryIcon from "@mui/icons-material/History";
import TuneIcon from "@mui/icons-material/Tune";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import GavelIcon from "@mui/icons-material/Gavel";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const MENU = [
  {
    type: "group",
    id: "configSistema",
    label: "Configurações do Sistema",
    icon: <TuneIcon fontSize="small" />,
    roles: ["SUPER"],
    dividerAfter: true,
    items: [
      {
        label: "Empresas",
        icon: <BusinessIcon fontSize="small" />,
        path: "/admin/empresas",
        roles: ["SUPER"],
      },
      {
        label: "Processos",
        icon: <AccountTreeIcon fontSize="small" />,
        path: "/admin/processos",
        roles: ["SUPER"],
      },
      {
        label: "Etapas do Processo",
        icon: <FormatListNumberedIcon fontSize="small" />,
        path: "/admin/etapas",
        roles: ["SUPER"],
      },
      {
        label: "Determinação de CFOP",
        icon: <CallSplitIcon fontSize="small" />,
        path: "/admin/cfop",
        roles: ["SUPER"],
      },
      {
        label: "Central de Mensagens",
        icon: <CampaignIcon fontSize="small" />,
        path: "/admin/mensagens",
        roles: ["SUPER"],
      },
      {
        label: "Configurações de Integração",
        icon: <VpnKeyIcon fontSize="small" />,
        path: "/admin/integration-configs",
        roles: ["SUPER"],
      },
      {
        label: "Termos de Uso",
        icon: <GavelIcon fontSize="small" />,
        path: "/admin/terms",
        roles: ["SUPER"],
      },
    ],
  },
  {
    type: "group",
    id: "configEmpresa",
    label: "Configurações da Empresa",
    icon: <ManageAccountsIcon fontSize="small" />,
    roles: ["SUPER", "ADMIN"],
    dividerAfter: true,
    items: [
      {
        label: "Certificados Digitais",
        icon: <VerifiedUserIcon fontSize="small" />,
        path: "/admin/certificados",
        roles: ["SUPER", "ADMIN"],
      },
      {
        label: "Usuários",
        icon: <PeopleIcon fontSize="small" />,
        path: "/admin/users",
        roles: ["SUPER", "ADMIN"],
      },
      {
        label: "Log de Atividades",
        icon: <HistoryIcon fontSize="small" />,
        path: "/admin/audit-log",
        roles: ["SUPER", "ADMIN"],
      },
    ],
  },
  {
    type: "group",
    id: "integracao",
    label: "Integração",
    icon: <SyncAltIcon fontSize="small" />,
    roles: ["SUPER", "ADMIN"],
    items: [
      {
        label: "Consultar XML na SEFAZ",
        icon: <FindInPageIcon fontSize="small" />,
        path: "/admin/xml-sefaz",
        roles: ["SUPER", "ADMIN"],
      },
      {
        label: "Logs de Integração",
        icon: <CloudSyncIcon fontSize="small" />,
        path: "/admin/integration",
        roles: ["SUPER", "ADMIN"],
      },
    ],
  },
];

const AdminDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [openGroups, setOpenGroups] = useState({});

  const hasAnyRole = (roles) => roles.some((r) => hasRole(r));

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 300,
          maxWidth: 300,
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#1e3a5f",
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminPanelSettingsIcon fontSize="small" />
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.3, color: "#fff" }}>
            Inbound Automation
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <List disablePadding sx={{ flex: 1, pt: 1 }}>
        {MENU.map((entry) => {
          const visibleItems = entry.items.filter((i) => hasAnyRole(i.roles));
          if (!visibleItems.length) return null;

          const isOpen = !!openGroups[entry.id];
          return (
            <Box key={entry.id}>
              <ListItemButton
                onClick={() => toggleGroup(entry.id)}
                sx={{ px: 2, py: 1, "&:hover": { backgroundColor: "action.hover" } }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "primary.main" }}>
                  {entry.icon}
                </ListItemIcon>
                <ListItemText disableTypography sx={{ minWidth: 0, overflow: "hidden" }}>
                  <Box sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4, color: "text.primary" }}>
                    {entry.label}
                  </Box>
                  <Box sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3, mt: 0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {visibleItems.map((i) => i.label).join(" · ")}
                  </Box>
                </ListItemText>
                {isOpen
                  ? <ExpandLess fontSize="small" sx={{ color: "text.secondary" }} />
                  : <ExpandMore fontSize="small" sx={{ color: "text.secondary" }} />
                }
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ backgroundColor: "action.hover" }}>
                  {visibleItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        pl: 3.5,
                        pr: 1,
                        py: 0.35,
                        minHeight: 28,
                        "&:hover": { backgroundColor: "action.selected" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 24, color: "text.disabled" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText disableTypography>
                        <Box sx={{ fontSize: 11, fontWeight: 400, color: "text.secondary", lineHeight: 1.3 }}>
                          {item.label}
                        </Box>
                      </ListItemText>
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
              {entry.dividerAfter && <Divider sx={{ my: 0.5 }} />}
            </Box>
          );
        })}
      </List>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.subtle",
        }}
      >
        <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "center" }}>
          TTFlow Inbound · Painel Admin
        </Typography>
      </Box>
    </Drawer>
  );
};

export default AdminDrawer;
